<script setup lang="ts">
import JobBookingFeaturedCard from '@/components/cards/JobBookingFeaturedCard.vue';
import JobBookingPageCard from '@/components/cards/JobBookingPageCard.vue';
import JobBookingRecentPanel from '@/components/panels/JobBookingRecentPanel.vue';
import JobBookingRecentPanelItem from '@/components/panels/JobBookingRecentPanelItem.vue';
import JobBookingSection from '@/components/sections/JobBookingSection.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import useHomeJobBookingViewData, {
  BOOKED_JOBS_PAGE_SIZE,
  DRAFTS_PAGE_SIZE,
} from '@/composables/data/useHomeJobBookingViewData';
import { useAuthStore } from '@/stores/useAuthStore';
import * as routeNames from '@/constants/routeNames';
import { brandConfig } from '@/brand';

const authStore = useAuthStore();
const {
  isFetchingDrafts,
  draftsTotal,
  draftsError,
  draftItems,
  isFetchingBookedJobs,
  bookedJobsTotal,
  bookedJobsError,
  bookedJobItems,
} = useHomeJobBookingViewData();
</script>

<template>
  <PageContentWrapper>
    <div :class="$style.homeJobBookingView" data-testid="homeJobBookingView">
      <template v-if="brandConfig.brand === 'kk'">
        <div :class="$style.row">
          <JobBookingFeaturedCard
            primary
            badge="Recommended"
            title="Book new job here"
            subtitle="Create jobs with complete control over stops, products, routing, and instructions. Use this when you need flexibility, multiple items, or complex logistics."
            :to="{ name: routeNames.ADVANCED_JOB_BOOKING }"
          />
          <JobBookingFeaturedCard
            badge="New"
            title="Try new bulk upload"
            subtitle="Upload multiple jobs at once using a spreadsheet template. Best for high-volume bookings or repeat jobs."
            :to="{ name: routeNames.SPREADSHEET_JOB_BOOKING }"
            outlined
          />
        </div>

        <JobBookingSection label="Warehouse Operations">
          <JobBookingPageCard
            disabled
            title="Pickup"
            subtitle="Create a simple job to collect items from a location and move them to the next destination."
            icon="mdi-wrench"
          />
          <JobBookingPageCard
            disabled
            title="Delivery"
            subtitle="Book a delivery to send items to a customer or site. Use this for standard drop-offs where the pickup details are already defined."
            icon="mdi-clipboard-text-outline"
          />
          <JobBookingPageCard
            disabled
            title="Warehouse → Customer"
            subtitle="Ship items directly from a warehouse to a customer location. Ideal when stock is already stored and ready for dispatch."
            icon="mdi-clipboard-text-outline"
          />
          <JobBookingPageCard
            disabled
            title="Warehouse → Warehouse"
            subtitle="Transfer items between warehouses or internal locations. Use this for stock movement, rebalancing, or internal logistics."
            icon="mdi-clipboard-text-outline"
          />
        </JobBookingSection>

        <JobBookingSection label="Other Booking">
          <JobBookingPageCard
            title="Spreadsheet (Bulk Upload)"
            subtitle="Upload multiple jobs at once using a spreadsheet template. Best for high-volume bookings or repeat jobs that can be prepared offline."
            icon="mdi-table-large"
            :to="{ name: routeNames.SPREADSHEET_JOB_BOOKING }"
          />
          <JobBookingPageCard
            v-if="authStore.isStaff"
            disabled
            title="Site Inspection"
            subtitle="Schedule a visit to assess a location. Use this for complex deliveries that require planning, measurements, or access checks."
            icon="mdi-account-check"
          />
        </JobBookingSection>

        <JobBookingSection label="Air Road">
          <JobBookingPageCard
            title="AirRoad jobs"
            subtitle="Book AirRoad jobs."
            icon="mdi-airplane"
            :to="{ name: routeNames.AIR_ROAD_JOBS }"
          />
          <JobBookingPageCard
            title="AirRoad booked"
            subtitle="Booked job's list"
            icon="mdi-history"
            :to="{ name: routeNames.AIR_ROAD_BOOKED }"
          />
        </JobBookingSection>
      </template>

      <template v-else-if="brandConfig.brand === 'pac'">
        <div :class="$style.row">
          <JobBookingFeaturedCard
            primary
            badge="Recommended"
            title="Book PAC job"
            subtitle="Pick up boxes of produce — choose a box, a fruit, and quantity."
            :to="{ name: routeNames.PAC_JOB_BOOKING }"
          />
          <JobBookingFeaturedCard
            title="Book pallets"
            subtitle="Pick up full pallets of produce — choose a fruit and quantity."
            :to="{ name: routeNames.PAC_PALLET_JOB_BOOKING }"
            outlined
          />
        </div>
        <div :class="$style.row">
          <JobBookingFeaturedCard
            title="Book boxes"
            subtitle="Order empty boxes delivered to the farm — choose a box type and quantity."
            :to="{ name: routeNames.PAC_BOOK_BOXES_JOB_BOOKING }"
            outlined
          />
        </div>
      </template>

      <div :class="$style.row">
        <JobBookingRecentPanel
          icon="mdi-history"
          title="Booked Jobs"
          :to="{ name: routeNames.BOOKED_JOBS }"
          :count="bookedJobsTotal"
          :loading="isFetchingBookedJobs"
          :skeleton-count="BOOKED_JOBS_PAGE_SIZE"
        >
          <div v-if="bookedJobsError" :class="$style.emptyPanel">
            Failed to load booked jobs
          </div>
          <div v-else-if="!bookedJobItems.length" :class="$style.emptyPanel">
            No data yet
          </div>
          <template v-else>
            <JobBookingRecentPanelItem
              v-for="bookedJob in bookedJobItems"
              :key="bookedJob.id"
              :label="bookedJob.label"
              :sublabel="
                bookedJob.sublabel ? `Booked ${bookedJob.sublabel}` : undefined
              "
              :meta="bookedJob.meta"
              :to="bookedJob.to"
            />
          </template>
        </JobBookingRecentPanel>
        <JobBookingRecentPanel
          icon="mdi-note-multiple-outline"
          title="Drafts"
          :to="{ name: routeNames.DRAFT_JOB_BOOKING }"
          :count="draftsTotal"
          :loading="isFetchingDrafts"
          :skeleton-count="DRAFTS_PAGE_SIZE"
        >
          <div v-if="draftsError" :class="$style.emptyPanel">
            Failed to load drafts
          </div>
          <template v-else>
            <JobBookingRecentPanelItem
              v-for="draft in draftItems"
              :key="draft.id"
              :label="draft.label"
              :sublabel="
                draft.sublabel ? `Edited ${draft.sublabel}` : undefined
              "
              :meta="draft.meta"
              :to="draft.to"
            />
          </template>
        </JobBookingRecentPanel>
      </div>
    </div>
  </PageContentWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.homeJobBookingView {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 1440px;
}
  .row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
  .emptyPanel {
    padding: 2rem 1.25rem;
    text-align: center;
    font-size: 0.875rem;
    color: colors.$color-text-tertiary;
  }
</style>
